/**
 * The findings list: every validation error and warning, each one a link
 * to the section that owns it.
 * @module components/editor/FindingsList
 */

import { isValidElement, useEffect, useMemo, useState } from "react";

import { Badge, countLabel, ListRow, Text } from "@/components/ui";
import { Icon } from "@/components/ui/Icon";
import { useProgramme } from "@/hooks/useStore";
import { lockedReason } from "@/state/step-locks";
import { activeSteps, isIdentityComplete, useEditorStore } from "@/state/store";
import type { CasAwardsData } from "@/types/cas";
import { isFeAwardType } from "@/utils/award-types";
import { loadCasData } from "@/utils/cas-data";
import { validateCasSelection, validateProgramme, type ValidationFlag } from "@/utils/validation";

/** Whether `flag` follows from a finding in `among`, and so is listed beneath it. */
function isNestedUnder(flag: ValidationFlag, among: readonly ValidationFlag[]): boolean {
  return Boolean(
    flag.nested &&
    flag.ploId &&
    among.some((parent) => parent !== flag && !parent.nested && parent.ploId === flag.ploId),
  );
}

/** Stable identity, so a pending or non-FE load does not churn the flag list. */
const NO_FLAGS: ValidationFlag[] = [];

/** Step definition from store */
interface Step {
  key: string;
  title: string;
  locked: boolean;
}

export interface FindingsListProps {
  /** Callback to navigate to a specific step, and the module it is about */
  onGoToStep: (stepKey: string, moduleId?: string) => void;
  /** Reports the current validation totals to a parent summary control. */
  onCountsChange?: (counts: { errors: number; warnings: number }) => void;
}

/**
 * Renders a single flag item.
 */
function FlagItem({
  flag,
  index,
  stepTitle,
  stepLocked,
  identityComplete,
  onGoToStep = undefined,
}: {
  flag: ValidationFlag;
  index: number;
  stepTitle: string;
  stepLocked: boolean;
  identityComplete: boolean;
  onGoToStep?: (stepKey: string, moduleId?: string) => void;
}) {
  const isClickable = Boolean(stepTitle && onGoToStep && !stepLocked);
  const isError = flag.type === "error";

  const handleClick = () => {
    if (isClickable && onGoToStep) {
      useEditorStore.getState().setSelection({
        ...(flag.versionId ? { selectedVersionId: flag.versionId } : {}),
        ...(flag.moduleId ? { selectedModuleId: flag.moduleId } : {}),
      });
      onGoToStep(flag.step, flag.moduleId);
      const content = document.getElementById("content");
      if (content) {
        content.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  return (
    <ListRow
      tone="none"
      onClick={isClickable ? handleClick : undefined}
      aria-label={
        isClickable
          ? `${isError ? "Error" : "Warning"}: ${flag.msg}. Click to go to ${stepTitle}`
          : undefined
      }
      data-testid={`flag-${flag.type}-${index}`}
    >
      <div className="flex flex-col items-start gap-1">
        <Badge tone={isError ? "error" : "warning"} variant="soft" size="sm">
          <Icon name="warning" aria-hidden /> {isError ? "ERROR" : "WARN"}
        </Badge>
        <Text as="div">{flag.msg}</Text>
        {stepTitle && !stepLocked ? (
          <Text as="div" tone="muted">
            {stepTitle}
          </Text>
        ) : null}
        {stepTitle && stepLocked ? (
          <Text as="div" tone="muted">
            <Icon name="lock-simple" aria-hidden /> {stepTitle}:{" "}
            {lockedReason(flag.step, identityComplete)}.
          </Text>
        ) : null}
      </div>
    </ListRow>
  );
}

/**
 * The findings list for the open programme.
 *
 * @example
 * ```tsx
 * <FindingsList onGoToStep={(stateKey) => navigateToSection(stateKey)} />
 * ```
 */
export function FindingsList({ onGoToStep, onCountsChange = undefined }: FindingsListProps) {
  const { programme } = useProgramme();
  const isFe = isFeAwardType(programme.awardType ?? "");

  // The catalogue once the load has settled: the data on success, `null` on
  // failure, which is what validateCasSelection reads as "no catalogue to check
  // against". `undefined` while the load is still in flight, so pending and
  // failed stay distinguishable and a pending load raises no flags.
  const [casCatalogue, setCasCatalogue] = useState<CasAwardsData | null | undefined>(undefined);

  // Keyed on the award type alone. The catalogue is the same file whatever the
  // programme says, so a mutation revalidates below without refetching it.
  useEffect(() => {
    if (!isFe) {
      return undefined;
    }

    let cancelled = false;
    loadCasData()
      .then((data) => {
        if (!cancelled) {
          setCasCatalogue(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCasCatalogue(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isFe]);

  const casFlags = useMemo(() => {
    if (!isFe || casCatalogue === undefined) {
      return NO_FLAGS;
    }
    return validateCasSelection(programme, casCatalogue);
  }, [isFe, casCatalogue, programme]);

  const flags = useMemo(
    () => [...(validateProgramme(programme) as ValidationFlag[]), ...casFlags],
    [programme, casFlags],
  );

  const steps = useMemo(() => activeSteps(programme) as Step[], [programme]);
  // Every locked row names why; Identity is checked once for all of them.
  const identityComplete = useMemo(() => isIdentityComplete(programme), [programme]);

  const errors = useMemo(() => flags.filter((f) => f.type === "error"), [flags]);
  const warnings = useMemo(() => flags.filter((f) => f.type === "warn"), [flags]);

  useEffect(() => {
    onCountsChange?.({ errors: errors.length, warnings: warnings.length });
  }, [errors.length, onCountsChange, warnings.length]);

  // Helper to get step title from step key
  const getStep = (stepKey: string): Step | undefined => steps.find((step) => step.key === stepKey);

  // No flags - show success message
  if (flags.length === 0) {
    return (
      <div data-testid="flags-container">
        <ListRow tone="none" data-testid="flag-ok">
          <div className="flex items-center gap-2">
            <Badge tone="success" variant="soft" size="sm">
              OK
            </Badge>
            <Text as="div">No flags. The programme looks good.</Text>
          </div>
        </ListRow>
      </div>
    );
  }

  // Build summary parts
  const summaryParts: React.ReactNode[] = [];
  if (errors.length > 0) {
    summaryParts.push(<span key="errors">{countLabel(errors.length, "error")}</span>);
  }
  if (warnings.length > 0) {
    summaryParts.push(<span key="warnings">{countLabel(warnings.length, "warning")}</span>);
  }

  return (
    <div className="flex flex-col gap-2" data-testid="flags-container">
      {/* Summary header */}
      <Text as="div" tone="muted" data-testid="flags-summary">
        {summaryParts.reduce<React.ReactNode[]>((acc, part) => {
          if (acc.length > 0) {
            const partKey = isValidElement(part) ? part.key : null;
            acc.push(<span key={`sep-before-${partKey}`}> · </span>);
          }
          acc.push(part);
          return acc;
        }, [])}
      </Text>

      {/* Flag items; a nested one sits under the finding it follows from. */}
      {flags.map((flag, idx) => {
        if (isNestedUnder(flag, flags)) {
          return null;
        }
        const renderFlag = (item: ValidationFlag, index: number) => {
          const step = getStep(item.step);
          return (
            <FlagItem
              key={`${item.type}-${item.step}-${item.msg}`}
              flag={item}
              index={index}
              stepTitle={step?.title ?? ""}
              stepLocked={step?.locked ?? false}
              identityComplete={identityComplete}
              onGoToStep={onGoToStep}
            />
          );
        };
        const children = flags
          .map((item, index) => ({ item, index }))
          .filter(({ item }) => item !== flag && isNestedUnder(item, [flag]));
        if (children.length === 0) {
          return renderFlag(flag, idx);
        }
        return (
          <div key={`${flag.type}-${flag.step}-${flag.msg}`} data-testid={`flag-group-${idx}`}>
            {renderFlag(flag, idx)}
            <div className="ms-6 mt-2 space-y-2">
              {children.map(({ item, index }) => renderFlag(item, index))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default FindingsList;
